import { DAY_MS, DayInterval, logicalDayStart, totalWorkMs } from "./rhythm";
import { median } from "./median";

// Cross-week "how do my weeks look?" projection for the Work-weeks panel.
// Pure functions, no store access. Builds on rhythm.ts's per-logical-day split
// (entriesByLocalDay → Map<logicalDayStart, DayInterval[]>) and re-buckets those
// logical days into Monday-anchored weeks and into calendar periods.
//
// ONE day rule end-to-end: every day here is a *logical* day (04:00 cutoff, from
// rhythm.ts). A logical day is assigned to the Monday-week — and to the month/
// quarter/year — of its logicalDayStart date. So a 01:00 session, which rhythm
// attributes to the previous logical day, also lands in that previous day's week
// and period. We never mix in calendar-midnight week math (analyticsPeriods.ts);
// this panel owns its own bucketing.

const HOUR_MS = 60 * 60 * 1000;

// Half-day model. A logical day's worked weight is a fraction of a "day" as
// calibrated by settings.dayHours:
//   < 0.25 × dayHours  → 0   (too little to count as worked)
//   < 0.60 × dayHours  → 0.5 (a half day)
//   otherwise          → 1   (a full day)
// The 0.25 floor matches rhythm's qualifying-day threshold; the 0.6 step lets a
// short day read as half a day so a week of five 3h days and a week of one 14h
// day don't both report the same "days worked".
export const HALF_DAY_FRACTION = 0.25;
export const FULL_DAY_FRACTION = 0.6;

// A week counts as a real work week (and so feeds the typical-week average) when
// its summed day-weight reaches this. Below it, the week is "light" — treated as
// time off and excluded from the typical figure but still shown on the strip and
// counted toward days-off.
export const ACTIVE_WEEK_MIN_DAYS = 3.5;

export type DayWeight = 0 | 0.5 | 1;

export function dayWeight(workMs: number, dayHours: number): DayWeight {
  if (dayHours <= 0) return 0;
  const frac = workMs / HOUR_MS / dayHours;
  if (frac < HALF_DAY_FRACTION) return 0;
  if (frac < FULL_DAY_FRACTION) return 0.5;
  return 1;
}

// Calendar-midnight start-of-day for an epoch ms. Used to normalise before the
// Monday/period walks so DST never shifts a boundary.
const startOfDay = (ms: number): number => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// Monday 00:00 of the week owning `ms`. Mon-anchored, matching the rest of the
// app: Sunday belongs to the week that started the previous Monday.
export const mondayWeekStart = (ms: number): number => {
  const d = new Date(startOfDay(ms));
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff).getTime();
};

// Shift a day-aligned epoch by whole calendar days, preserving the wall-clock
// time-of-day across DST (mirrors rhythm.ts's day walk).
export const addDays = (ms: number, n: number): number => {
  const d = new Date(ms);
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + n,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
    d.getMilliseconds(),
  ).getTime();
};

// Mon-anchored weekday index: Mon=1 … Sat=6, Sun=7 (so Sunday sorts last).
const mondayIndex = (ms: number): number => {
  const dow = new Date(ms).getDay();
  return dow === 0 ? 7 : dow;
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Human label for a Monday-anchored week: "Jun 9–15" within a month,
// "Jun 30–Jul 6" across a month, "Dec 29–Jan 4" across a year. The year is
// appended only when the week falls outside the current year (relative to
// `now`), so the common case stays terse. Week end is weekStart + 6 calendar
// days via addDays so DST never shifts it.
export function formatWeekRange(weekStart: number, now: number): string {
  const s = new Date(weekStart);
  const e = new Date(addDays(weekStart, 6));
  const sameMonth =
    s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const base = sameMonth
    ? `${MONTHS[s.getMonth()]} ${s.getDate()}–${e.getDate()}`
    : `${MONTHS[s.getMonth()]} ${s.getDate()}–${MONTHS[e.getMonth()]} ${e.getDate()}`;
  const currentYear = new Date(now).getFullYear();
  const outsideYear =
    s.getFullYear() !== currentYear || e.getFullYear() !== currentYear;
  return outsideYear ? `${base}, ${e.getFullYear()}` : base;
}

export type WeekStatus = "active" | "light" | "partial";

export interface WeekStat {
  // Monday 00:00 epoch ms of the week.
  weekStart: number;
  // Summed day-weight across the week's logical days (0..7, in 0.5 steps).
  workedDays: number;
  // Total worked ms in the week, from the hours-source day map (smoothed if the
  // caller passed a smoothed map). Drives the median-hours figure.
  workMs: number;
  // "partial" = the current in-progress week (excluded from the typical
  // average). "active"/"light" split fully-elapsed weeks at ACTIVE_WEEK_MIN_DAYS.
  status: WeekStatus;
  isCurrent: boolean;
}

const sumWeightsByWeek = (
  byDay: Map<number, DayInterval[]>,
  dayHours: number,
): Map<number, number> => {
  const out = new Map<number, number>();
  for (const [dayStart, ivs] of byDay) {
    const wk = mondayWeekStart(dayStart);
    out.set(wk, (out.get(wk) ?? 0) + dayWeight(totalWorkMs(ivs), dayHours));
  }
  return out;
};

const sumMsByWeek = (
  byDay: Map<number, DayInterval[]>,
): Map<number, number> => {
  const out = new Map<number, number>();
  for (const [dayStart, ivs] of byDay) {
    const wk = mondayWeekStart(dayStart);
    out.set(wk, (out.get(wk) ?? 0) + totalWorkMs(ivs));
  }
  return out;
};

// Build the last `lookbackWeeks` Monday-weeks (newest first), stopping at the
// first week that has any tracked data so weeks before the user started don't
// masquerade as vacation. The current week is tagged "partial".
//
// byDayWeights drives the worked-day weights (use the RAW day map — smoothing
// barely moves a binary day classification). byDayHours drives the hours figure
// (pass the reporting-smoothed map so hours reconcile with billing/Rhythm).
export function buildWeekStats(
  byDayWeights: Map<number, DayInterval[]>,
  byDayHours: Map<number, DayInterval[]>,
  now: number,
  lookbackWeeks: number,
  dayHours: number,
): WeekStat[] {
  const currentWeek = mondayWeekStart(logicalDayStart(now));

  let firstWeek: number | null = null;
  for (const dayStart of byDayWeights.keys()) {
    const wk = mondayWeekStart(dayStart);
    if (firstWeek === null || wk < firstWeek) firstWeek = wk;
  }

  const weightByWeek = sumWeightsByWeek(byDayWeights, dayHours);
  const msByWeek = sumMsByWeek(byDayHours);

  const weeks: WeekStat[] = [];
  let wk = currentWeek;
  for (let i = 0; i < lookbackWeeks; i++) {
    if (firstWeek !== null && wk < firstWeek) break;
    const workedDays = weightByWeek.get(wk) ?? 0;
    const isCurrent = wk === currentWeek;
    const status: WeekStatus = isCurrent
      ? "partial"
      : workedDays >= ACTIVE_WEEK_MIN_DAYS
        ? "active"
        : "light";
    weeks.push({
      weekStart: wk,
      workedDays,
      workMs: msByWeek.get(wk) ?? 0,
      status,
      isCurrent,
    });
    wk = addDays(wk, -7);
  }
  return weeks;
}

export interface TypicalWeek {
  // Median over active weeks. Days and hours are independent medians, so they
  // may not describe the same week — present them as two facts, not one.
  medianDays: number;
  medianHours: number;
  // Active weeks counted, out of fully-elapsed weeks considered (the badge).
  activeCount: number;
  elapsedCount: number;
}

export function typicalWeekStats(weeks: WeekStat[]): TypicalWeek {
  const elapsed = weeks.filter((w) => w.status !== "partial");
  const active = weeks.filter((w) => w.status === "active");
  return {
    medianDays: median(active.map((w) => w.workedDays)),
    medianHours: median(active.map((w) => w.workMs / HOUR_MS)),
    activeCount: active.length,
    elapsedCount: elapsed.length,
  };
}

export type CadencePeriod = "month" | "quarter" | "year";

// Calendar start (00:00) of the period containing `now`.
export function periodStart(now: number, period: CadencePeriod): number {
  const d = new Date(now);
  if (period === "month") return new Date(d.getFullYear(), d.getMonth(), 1).getTime();
  if (period === "quarter") {
    const qMonth = Math.floor(d.getMonth() / 3) * 3;
    return new Date(d.getFullYear(), qMonth, 1).getTime();
  }
  return new Date(d.getFullYear(), 0, 1).getTime();
}

// Count Mon-anchored planned workdays in [startMs, endMs). Walks day by day
// (DST/leap-safe via calendar-day construction) rather than multiplying a
// fraction, so a part-elapsed period yields exactly the planned days so far.
export function expectedWorkdays(
  startMs: number,
  endMs: number,
  workdaysPerWeek: number,
): number {
  let count = 0;
  let cursor = startOfDay(startMs);
  while (cursor < endMs) {
    if (mondayIndex(cursor) <= workdaysPerWeek) count++;
    cursor = addDays(cursor, 1);
  }
  return count;
}

// Summed day-weight for logical days whose logicalDayStart falls in [start, end).
const workedDaysInRange = (
  byDay: Map<number, DayInterval[]>,
  startMs: number,
  endMs: number,
  dayHours: number,
): number => {
  let sum = 0;
  for (const [dayStart, ivs] of byDay) {
    if (dayStart >= startMs && dayStart < endMs)
      sum += dayWeight(totalWorkMs(ivs), dayHours);
  }
  return sum;
};

const workMsInRange = (
  byDay: Map<number, DayInterval[]>,
  startMs: number,
  endMs: number,
): number => {
  let sum = 0;
  for (const [dayStart, ivs] of byDay) {
    if (dayStart >= startMs && dayStart < endMs) sum += totalWorkMs(ivs);
  }
  return sum;
};

// A period window shorter than this many days of tracked history is too thin
// to read a meaningful days-off figure from (a brand-new user, or the first few
// days of a fresh year/quarter). The panel shows a "not enough history yet"
// notice instead of a swingy number.
export const SHORT_HISTORY_DAYS = 14;

export interface PeriodStats {
  expectedDays: number;
  workedDays: number;
  // Planned days with no tracked work. Clamped at 0 — working an unplanned day
  // (e.g. a Saturday for a Mon–Fri schedule) can push worked past expected, and
  // "negative days off" is meaningless. Honest for a user who tracks every
  // working day: absence = a real day not worked.
  daysOff: number;
  workMs: number;
  // The actual start of the window the numbers were computed over: the calendar
  // period start, OR the first tracked day when that's later (so weekdays before
  // the user ever tracked don't read as days off).
  windowStart: number;
  // True when windowStart was pulled forward to the first tracked day, i.e. the
  // period predates the user's history. Drives the "since <date>" caption.
  clamped: boolean;
  // True when the window is clamped AND under SHORT_HISTORY_DAYS of history —
  // too little to quote a days-off figure. Drives the suppression notice.
  shortHistory: boolean;
}

// Earliest tracked logical-day key, or null for an empty map.
const firstTrackedDay = (byDay: Map<number, DayInterval[]>): number | null => {
  let first: number | null = null;
  for (const k of byDay.keys()) if (first === null || k < first) first = k;
  return first;
};

export function periodStats(
  byDayWeights: Map<number, DayInterval[]>,
  byDayHours: Map<number, DayInterval[]>,
  now: number,
  period: CadencePeriod,
  workdaysPerWeek: number,
  dayHours: number,
): PeriodStats {
  const calStart = periodStart(now, period);
  // Clamp the window start to the first tracked day when the period reaches back
  // before the user started tracking — otherwise every planned weekday in the
  // untracked span reads as a day off (the Year period's over-count). Mirrors
  // the first-data clamp buildWeekStats already applies to the strip.
  // Compare on calendar-day granularity: `first` is a logical-day key (04:00),
  // calStart a calendar midnight, so a raw `first > calStart` would spuriously
  // "clamp" when the first tracked day IS the period-start day.
  const first = firstTrackedDay(byDayWeights);
  const clamped = first !== null && startOfDay(first) > calStart;
  const start = clamped ? first : calStart;

  // Plan only counts through the end of yesterday. Today is still in progress,
  // so counting it as a planned day the moment the clock passes midnight would
  // report a phantom day off every weekday morning until enough work accrues.
  // Worked days and hours still include today, so today's work can only ever
  // reduce days-off, never inflate it.
  const expectedDays = expectedWorkdays(start, startOfDay(now), workdaysPerWeek);
  const workedDays = workedDaysInRange(byDayWeights, start, now, dayHours);
  // Round the span to whole days so a DST transition inside it can't nudge the
  // threshold by an hour.
  const historyDays = Math.round(
    (startOfDay(now) - startOfDay(start)) / DAY_MS,
  );
  const shortHistory = clamped && historyDays < SHORT_HISTORY_DAYS;
  return {
    expectedDays,
    workedDays,
    daysOff: Math.max(0, expectedDays - workedDays),
    workMs: workMsInRange(byDayHours, start, now),
    windowStart: start,
    clamped,
    shortHistory,
  };
}
